<script setup lang="ts">
const route = useRoute()
const preferencesStore = usePreferencesStore()
const publicPaths = ['/assets/', '/connected/', '/undefined'] // TODO: Add paths that skips guest login
const path = ref(route.path.replace(/^\/fr\//, '/'))

const hideBreadCrumbs = computed(() => path.value.includes('/register') || path.value.includes('/account-confirmation'))
const hideSideMenu = computed(() => path.value.includes('/register') || path.value.includes('/account-confirmation'))

onMounted(() => {
  // Third-party tags wait for hydration and an idle main thread rather than competing with them.
  const integrationScripts = [generateOptimizelyEntryScript(), generateBarillianceScript(), generateTradeDeskScript()]
  onNuxtReady(() => injectClientSideScripts(integrationScripts))

  setupHeapAnalytics()

  // Link Salesforce Customer ID to analytics platforms using the shared composable
  const { linkCustomerId } = useAnalyticsIdentity()
  linkCustomerId()
})

watch(
  () => route.path,
  (newPath) => {
    path.value = newPath.replace(/^\/fr\//, '/')
  },
  { immediate: true },
)

if (import.meta.client && !publicPaths.includes(route.path)) {
  await initLayoutStores()
}

useHead(() => {
  const googleVerificationToken = preferencesStore.site?.googleVerificationToken
  if (import.meta.server && googleVerificationToken) {
    return {
      meta: [
        {
          key: 'google-site-verification',
          name: 'google-site-verification',
          content: googleVerificationToken,
        },
      ],
    }
  }
  return {}
})
</script>

<template>
  <Header />
  <main id="main">
    <PageContentSlot class="my-account__header-slot" :slug="path" section="header" />
    <div class="container">
      <div class="my-account">
        <client-only>
          <SideMenu class="my-account__side-menu" v-if="!hideSideMenu" />
          <div class="my-account__page-content">
            <NuxtPage />
          </div>
        </client-only>
      </div>
    </div>
    <GlobalIncludes />
  </main>
  <PageContentSlot :slug="path" section="footer" />
  <Footer />
</template>

<style lang="postcss">
.my-account {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: var(--space-50);
  width: 100%;

  @media (--tablet) {
    padding-top: var(--space-30);
    margin-top: var(--space-70);
    flex-direction: row;
    gap: 8rem;
    align-items: flex-start;
  }

  .my-account__side-menu {
    box-sizing: border-box;
    margin-top: var(--space-70);
    flex-shrink: 0;
    width: 100%;

    @media (--mobile-only) {
      display: contents;

      > .side-menu__title {
        display: none;
      }

      .side-menu-card__list {
        margin-top: var(--space-100);
      }
    }

    @media (--tablet) {
      width: 16rem;
      margin-top: 0;
    }

    &.side-menu--sub-page {
      margin-top: 0;
    }
  }

  .my-account__page-content {
    width: 100%;
    min-width: 0;
    order: -1;

    @media (--tablet) {
      order: unset;
      padding-top: var(--space-110);
    }
  }

  &:not(:has(.side-menu--sub-page)) > .my-account__page-content {
    display: contents;

    @media (--tablet) {
      display: block;
    }
  }

  &:has(.side-menu--sub-page) > .my-account__page-content {
    order: unset;

    @media (--tablet) {
      padding-top: var(--space-120);
    }
  }
}
</style>
